<script lang="ts" setup>
    import VueDatePicker from '@vuepic/vue-datepicker';
    import '@vuepic/vue-datepicker/dist/main.css';
    import { isBoolean } from 'lodash';
    import { watch, ref, computed, onMounted, onUnmounted } from 'vue';
    import {HtmlChangeEventType, DateDisplayFormat, IconTypes, TextDisplayStyle  } from "../Enums/UiEnums";    
    import { ValidationResults } from '../lib/Models/ValidationResults';
    import { toDisplayDateString, NoTzDate, addDay, addMonth, dateFormatterString, getDateParts, dateFromParts, isValidDate } from '../lib/DateUtils';
    import { isDate } from "lodash";
    import Icon from './Icon.vue';
    import TextInput from './TextInput.vue';

    type keyEvt = (evt: KeyboardEvent) => void;
 
    const props = withDefaults(defineProps<{
            value?: string,
            clearable?: boolean,
            required?: boolean,
            errorDisplay?: boolean,
            displayFormat?: DateDisplayFormat
            internalDataFormat?: DateDisplayFormat
            placeholder?: string
            disabled?:boolean,
            inputClassName?: string,
            keyDownCustomFunction?: keyEvt,
            cssFramework?: "bootstrap" | "vanilla",
            centerCalendarPopup?: boolean,
            textExtraClass?: string,
            validStartDate?: Date,
            validEndDate?: Date,            
            validator?: (normalizedValue: any, evt?: HtmlChangeEventType) => boolean | ValidationResults,
        }>(), {
                clearable: true,
                required: false,
                displayFormat: DateDisplayFormat.NumericDashDate,
                errorDisplay: true,
                disabled: false,
                centerCalendarPopup: false,
                cssFramework: "bootstrap",
                validStartDate: () => new Date("Dec 31, 1875"),
                validEndDate: () => new Date("Jan 1, 2200")
            });

    defineExpose({ focus });            

    enum HotKeyCodes
    {
        TodaysDate = "KeyT",
        PopUpCalendar = "KeyC",
        //WorkingDate = "KeyW",
        Clear = "KeyL",
        AddDay = "ArrowUp",
        SubtractDay = "ArrowDown",
        NumpadSubtract = "NumpadSubtract",
        NumPadAdd = "NumpadAdd",
        Escape = "Escape",
        Tab = "Tab"
    }

    const textInput = ref();    // Text input for date
    const dateInput = ref();    // Date picker component
    const internalDateValue = ref<Date>();
    const validationErrorMsg = ref<string>();
    const isValid = ref<boolean>();
    const menuIsOpen = ref<boolean>(false);
    const requiredText = "Required";
    const isErrorDisplay = computed<boolean>(() => props.errorDisplay === true); 
    const validationTooltipMessage = ref<string>();
    const validationClass = computed(() =>{
        return [
            false === isValid.value ? "Invalid" : "", 
        ];
    });
    
    const emit = defineEmits<{
        (e: "update:value", strValue: NoTzDate|number|undefined): void,
        (e: "validationState", state: true|false|"unmounted"|undefined, msg?: string): void
    }>();

    const internalDateString = computed<string|undefined>(()=> 
    {
        return toUserFacingDateString(internalDateValue.value);
    });

    onMounted(() => {
        updateInternalDateValue(props.value);
        
        if (props.value != internalDateString.value)
            emitInternalDate(true);

        // Since we don't have any data yet, indicate we're invalid at the start
        if (props.required && props.value === undefined)
        {
            emitValidationState(false, requiredText);
        }
        else if ((props.value) && (props.validator)) {
            const propsDate = new Date(props.value);
            checkValidator(propsDate);
        }
    });

    onUnmounted(() => {
        emit("validationState", "unmounted");
    });

    watch(() => props.value, (newValue, _oldValue) => {
        if (newValue) {
            isValid.value = true;
        }
        else {
            isValid.value = false;
        }
        updateInternalDateValue(newValue);
        emitInternalDate(false);
    });

    //computed function for "base" keyHandler - enable certain shortcut keys by default on our date control. 
    //Override this by passing in a custom function with keyDownCustomFunction prop.
    function defaultEventKeyHandler(event: KeyboardEvent) : void
    {
        // Are there any Ctrl+keys?
        if (event.ctrlKey)
        {
            switch(event.code)
            {
                // CTRL and + Key will increment the date in the field up 1 month
                case HotKeyCodes.NumPadAdd:
                event.preventDefault();
                nudgeInternalDate(1, "month");
                return;

                // CTRL and - Key will decrement the date in the field down 1 month
                case HotKeyCodes.NumpadSubtract:
                event.preventDefault();
                nudgeInternalDate(-1, "month");
                return;
            }
        }

        const keyDict: Record<HotKeyCodes, () => Date|undefined> = 
        {
            [HotKeyCodes.TodaysDate]: () => { dateInput.value?.closeMenu(); return new Date(); },
            [HotKeyCodes.Clear]: () => { onChange(undefined); return undefined; },
            [HotKeyCodes.AddDay]: () => nudgeDate(1, "day"),
            [HotKeyCodes.NumPadAdd]: () => nudgeDate(1, "day"),
            [HotKeyCodes.SubtractDay]: () => nudgeDate(-1, "day"),
            [HotKeyCodes.NumpadSubtract]: () => nudgeDate(-1, "day"),
            [HotKeyCodes.PopUpCalendar]: () => { toggleCalendarMenu(); return internalDateValue.value; },
            [HotKeyCodes.Escape]: () => { dateInput.value?.closeMenu(); return internalDateValue.value; },
            [HotKeyCodes.Tab]: () => internalDateValue.value
        };

        const keyPressFunc = keyDict[<HotKeyCodes>event.code];
        if (keyPressFunc)
        {
            event.preventDefault();
            internalDateValue.value = keyPressFunc();
            emitInternalDate(true);
        }
    };

    function toggleCalendarMenu(): void
    {
        // Toggle menu based on current state
        if (!menuIsOpen.value)
            dateInput.value?.openMenu();  
        else
            dateInput.value?.closeMenu();  
    }

    function onTextBoxKeyDown(evt: KeyboardEvent)
    {
        // Keys that can be confused with num-pad keys -- just keeps the input cleaner
        if (!evt.ctrlKey && ("+" === evt.key || "-" === evt.key || "=" === evt.key || "_" === evt.key))
        {
            evt.preventDefault();
            return;
        }

        // When tabbing off the calendar
        if (HotKeyCodes.Tab === evt.code)
        {
            dateInput.value?.closeMenu();
        }

        if (props.keyDownCustomFunction)
            props.keyDownCustomFunction(evt);
        else
            defaultEventKeyHandler(evt);
    }

    function nudgeDate(valueToAddSubtract: number, dateType: "day"|"month"): Date
    {
        if (undefined === internalDateValue.value)
            return new Date();

        switch(dateType)
        {
            case "day":
            return addDay(internalDateValue.value, valueToAddSubtract);

            case "month":
            return addMonth(internalDateValue.value, valueToAddSubtract);

            default:
            alert("Unhandled dateType: "+ dateType);
            return internalDateValue.value;
        }
    }

    function nudgeInternalDate(valueToAddSubtract: number, dateType: "day"|"month"): Date
    {
        const newDate = nudgeDate(valueToAddSubtract, dateType);
        internalDateValue.value = newDate;
        emitInternalDate(true);
        return internalDateValue.value;
    }
    
    function updateInternalDateValue(dateValue: Date|string|undefined)
    {
        if ("string" === typeof(dateValue))
        {
            internalDateValue.value = dateFromParts(getDateParts(dateValue));
        }
        else if (isDate(dateValue))
        {
            if (isValidDate(dateValue?.toString()))
            {
                internalDateValue.value = dateValue;
            }
        }
        else if (undefined == dateValue)
        {            
            internalDateValue.value = undefined;
        }
    }

    function toUserFacingDateString(dateVal: Date|undefined): string|undefined{
        return toDisplayDateString(dateVal, props.displayFormat);
    }
    
    const datePickerInputOptions = computed(() => 
    {
        return {format: dateFormatterString(props.displayFormat), enterSubmit: true, tabSubmit: true };
    });

    function onChange(date?: Date){ 
        if (!date || isValidDate(date?.toString()))
        {
            updateInternalDateValue(date);
            emitInternalDate(true);

            emitValidationState(true);

            // Is the date within the valid range?
            if (date)
            {
                validateDate(date);
            }
        }
        else
        {
            emitValidationState(false, "Invalid Date");
        }
    }

    function setMenuState(isOpen: boolean)
    {
        menuIsOpen.value = isOpen;
    }

    function emitInternalDate(emit_update : boolean) {
        
        var isInternalDateValid = !Number.isNaN(internalDateValue.value?.getDate());

        const currentDataString = isInternalDateValid ? toDisplayDateString(internalDateValue.value, props.internalDataFormat ?? props.displayFormat) : undefined; 

        if (emit_update) {
            // Check if we have something valid to emit?
            if ((!internalDateValue.value) || (currentDataString?.length && isInternalDateValid))
            {               
                emit("update:value", currentDataString);                 
            }
        }    

        // // Validate data
        // let isValidLocal: boolean = true;
        // let validationErrorMsgLocal: string|undefined;
        // if (props.required && undefined === currentDataString)
        // {
        //     isValidLocal = false;
        //     validationErrorMsgLocal = requiredText;
        // }
        // else if (!isInternalDateValid)
        // {
        //     isValidLocal = false;
        //     validationErrorMsgLocal = "Invalid Date";            
        // }
        
        // // Indicate if we're in a valid state or not 
        // isValid.value = isValidLocal;
        // validationErrorMsg.value = validationErrorMsgLocal;
        // emitValidationState(isValid.value, validationErrorMsg.value); 
        validateDate(internalDateValue.value);
    }

    function validateDate(date?: Date)
    {
        // Is the date within the valid range?
        if (date)
        {
            if (props.validStartDate && date < props.validStartDate)
            {
                emitValidationState(false, "Before expected start date");
                return;
            }
            if (props.validEndDate && date > props.validEndDate)
            {
                emitValidationState(false, "After expected end date");
                return;
            }
        }
        if (undefined == date)
        {
            if (props.required)
            {
                emitValidationState(false, requiredText); 
                return;
            }
        }
        if (checkValidator(date)) { 
            return;
        }
        emitValidationState(true);
    }
    function checkValidator(date? : Date) : boolean {
        if (!props.validator) {
            return false;
        }
            
        let isValidLocal: boolean = true;  
        let validationErrorMsgLocal: string|undefined;
        const validationResult = props.validator(date);
        if (isBoolean(validationResult))
        {
            isValidLocal = validationResult;
        }
        else
        {                
            isValidLocal = validationResult.isValid;
            validationErrorMsgLocal = validationResult.errorMsg;
        }
        emitValidationState(isValidLocal, validationErrorMsgLocal); 
        return true;
    }

    function emitValidationState(isValidState: boolean, validationErrorMsg?: string)
    {
        isValid.value = isValidState;
        validationTooltipMessage.value = validationErrorMsg;
        emit("validationState", isValidState, validationErrorMsg); 
    }

    function FormattedDate(date: unknown): string | undefined  {
        if (undefined == date)
            return;
        
        if (isDate(date))
        {
            if (Number.isNaN(date.getDate()))        
            {
                return toUserFacingDateString(internalDateValue.value);    
            }
            return toUserFacingDateString(date);
        }
        alert("Unexpected!!");
    }

    function changedDateText(dateValue: string|number|undefined)
    {
        // Don't allow numbers in
        if ("number" === typeof(dateValue))
        {
            alert("Bad data type");
            return;
        }

        // Parse the date
        const dateParts = getDateParts(dateValue);
        if (undefined === dateParts)
        {
            dateInput.value.clearValue();
            onChange(undefined);
            return;    
        }

        const newDate = dateFromParts(dateParts);
        dateInput.value.updateInternalModelValue(newDate);
        dateInput.value.selectDate();
        dateInput.value.closeMenu();
        onChange(newDate);
    }

    const shortcutInfo = 
        "T => Today\n" +
        "L => Clear Date" +
        "C => Show/Hide Calendar\n" +
        "Esc => Hide Calendar\n" +
        "Up Arrow or Numpad + => Add One Day\n" +
        "Down Arrow or Numpad - => Subtract One Day\n" +
        "Ctrl + => Add One Month\n" +
        "Ctrl - => Subtract One Month\n";

    const textInputToolTip = computed<string>(() => {
        if (!isValid.value)
        {
            return `INVALID: ***${validationTooltipMessage.value?.trim()}***\n\n${shortcutInfo}`
        }
        if (props.disabled) {
            return;  
        }
        return shortcutInfo;
    });

    function focus()
    {
        textInput.value?.focus();
    }

    const inputPlaceholderText = computed<string|undefined>(() =>{
        if (undefined != props.placeholder)
            return props.placeholder;

        switch (props.displayFormat)
        {
            case DateDisplayFormat.NumericDashDate:
                return "MM-DD-YYYY";
            case DateDisplayFormat.NumericDate:
                return "MM/DD/YYYY";
            case DateDisplayFormat.PrettyDate:
                return "MMM DD, YYYY";
        }
    });
</script>

<style scoped>
    .Invalid{border: 2px solid red; border-radius: 4px; }
    .ErrorMsg{ color:crimson}
</style>
<style >
    .small_date {
        background-color: var(--dp-background-color);
        border-radius: 4px;
        font-family: -apple-system,blinkmacsystemfont,"Segoe UI",roboto,oxygen,ubuntu,cantarell,"Open Sans","Helvetica Neue",sans-serif;
        border: 1px solid var(--dp-border-color);
        outline: none;
        transition: border-color .2s cubic-bezier(0.645, 0.045, 0.355, 1);
        width: 100%;
        font-size: .75rem;
        line-height: .75rem;
        padding: 6px 12px 6px 30px;
        color: var(--dp-text-color);
        box-sizing: border-box;
    }
</style>

<template>
    <VueDatePicker :class="validationClass"
        ref="dateInput"
        :modelValue="internalDateValue" 
        :textInput="true" 
        :format="FormattedDate" 
        @update:modelValue="onChange"
        @open="() => setMenuState(true)"
        @closed="() => setMenuState(false)"
        :clearable="clearable" 
        :textInputOptions="datePickerInputOptions" 
        :closeOnScroll="true"
        :autoApply="true"
        :placeholder="inputPlaceholderText"
        :required="props.required"
        :inputClassName="props.inputClassName"
        :enableTimePicker="false"
        :disabled="props.disabled"
        :teleportCenter="props.centerCalendarPopup">
        
        <template #dp-input="{ value, onInput, onEnter, onTab, onClear, onBlur, onKeypress, onPaste, isMenuOpen }">
            <TextInput 
                    ref="textInput"
                    :textValue="value"
                    @update:textValue="changedDateText" 
                    @keydown="onTextBoxKeyDown"
                    :title="textInputToolTip"
                    :disabled="props.disabled"
                    :extraClass = "props?.textExtraClass"
                    :cssFramework="props.cssFramework">
                <template #LeftAddon>
                    <Icon :icon="IconTypes.Calendar" :displayStyle="TextDisplayStyle.Muted" />
                </template>
            </TextInput>
          </template>                
    </VueDatePicker>

    <div v-if="validationErrorMsg && isErrorDisplay" class="ErrorMsg">
        {{validationErrorMsg}}
    </div>
</template>